(() => {
  const sectionControllers = new WeakMap();
  const quickAddTimers = new WeakMap();
  const storagePrefix = 'fill-new-season-active:';

  function getTabs(section) {
    return Array.from(section.querySelectorAll('[data-fill-new-season-tab]'));
  }

  function getPanels(section) {
    return Array.from(section.querySelectorAll('[data-fill-new-season-panel]'));
  }

  function getActiveTab(section) {
    return getTabs(section).find((tab) => tab.getAttribute('aria-selected') === 'true') || getTabs(section)[0];
  }

  function setScrollButtonState(panel) {
    const scroller = panel.querySelector('[data-fill-new-season-products]');
    const previous = panel.querySelector('[data-fill-new-season-prev]');
    const next = panel.querySelector('[data-fill-new-season-next]');

    if (!scroller || !previous || !next) return;

    const maxScroll = scroller.scrollWidth - scroller.clientWidth;
    const atStart = scroller.scrollLeft <= 2;
    const atEnd = scroller.scrollLeft >= maxScroll - 2;

    previous.disabled = maxScroll <= 0 || atStart;
    next.disabled = maxScroll <= 0 || atEnd;
  }

  function updateAllScrollButtons(section) {
    getPanels(section).forEach(setScrollButtonState);
  }

  function activateTab(section, tab, options = {}) {
    if (!tab) return;

    const blockId = tab.dataset.fillNewSeasonBlockId;
    const tabs = getTabs(section);
    const panels = getPanels(section);

    tabs.forEach((candidate) => {
      const isActive = candidate === tab;
      candidate.setAttribute('aria-selected', isActive ? 'true' : 'false');
      candidate.tabIndex = isActive ? 0 : -1;
    });

    panels.forEach((panel) => {
      const isActive = panel.dataset.fillNewSeasonBlockId === blockId;
      panel.removeAttribute('data-fill-new-season-panel-state');

      if (isActive) {
        panel.hidden = false;
        panel.dataset.fillNewSeasonPanelState = 'entering';

        requestAnimationFrame(() => {
          panel.dataset.fillNewSeasonPanelState = 'active';
          setScrollButtonState(panel);
        });
      } else {
        panel.hidden = true;
      }
    });

    if (options.focus) {
      tab.focus();
    }

    try {
      window.sessionStorage.setItem(storagePrefix + section.id, blockId || '');
    } catch (error) {
      // Storage can be unavailable in strict privacy contexts.
    }
  }

  function getQuickAddButton(formComponent) {
    return formComponent?.querySelector?.('.fill-new-season__quick-control--add');
  }

  function clearQuickAddTimer(button) {
    const timer = quickAddTimers.get(button);
    if (timer) {
      window.clearTimeout(timer);
      quickAddTimers.delete(button);
    }
  }

  function setQuickAddState(button, state) {
    if (!button) return;

    clearQuickAddTimer(button);

    if (state === 'loading') {
      button.dataset.fillNewSeasonState = 'loading';
      button.setAttribute('aria-busy', 'true');
      button.disabled = true;
      return;
    }

    if (state === 'added') {
      button.dataset.fillNewSeasonState = 'added';
      button.removeAttribute('aria-busy');
      button.disabled = false;

      const timer = window.setTimeout(() => {
        button.removeAttribute('data-fill-new-season-state');
        quickAddTimers.delete(button);
      }, 1500);

      quickAddTimers.set(button, timer);
      return;
    }

    button.removeAttribute('data-fill-new-season-state');
    button.removeAttribute('aria-busy');
    button.disabled = false;
  }

  function resetSectionQuickAdd(section) {
    section.querySelectorAll('.fill-new-season__quick-control--add').forEach((button) => {
      clearQuickAddTimer(button);
      button.removeAttribute('data-fill-new-season-state');
      button.removeAttribute('aria-busy');
      if (!button.hasAttribute('aria-disabled')) {
        button.disabled = false;
      }
    });
  }

  function focusAdjacentTab(section, direction) {
    const tabs = getTabs(section);
    if (tabs.length === 0) return;

    const activeElement = document.activeElement;
    const currentIndex = tabs.indexOf(activeElement);
    const fallbackIndex = Math.max(0, tabs.indexOf(getActiveTab(section)));
    const startIndex = currentIndex >= 0 ? currentIndex : fallbackIndex;
    const nextIndex = (startIndex + direction + tabs.length) % tabs.length;

    tabs[nextIndex].focus();
  }

  function scrollPanel(panel, direction) {
    const scroller = panel.querySelector('[data-fill-new-season-products]');
    if (!scroller) return;

    const distance = Math.max(220, Math.round(scroller.clientWidth * 0.82));
    scroller.scrollBy({
      left: direction * distance,
      behavior: window.matchMedia('(prefers-reduced-motion: reduce)').matches ? 'auto' : 'smooth',
    });
  }

  function restoreActiveTab(section) {
    let storedBlockId = '';

    try {
      storedBlockId = window.sessionStorage.getItem(storagePrefix + section.id) || '';
    } catch (error) {
      storedBlockId = '';
    }

    if (!storedBlockId) return;

    const tab = getTabs(section).find((candidate) => candidate.dataset.fillNewSeasonBlockId === storedBlockId);
    if (tab) {
      activateTab(section, tab);
    }
  }

  function initSection(section) {
    sectionControllers.get(section)?.abort();

    const controller = new AbortController();
    const { signal } = controller;
    sectionControllers.set(section, controller);

    const tabs = getTabs(section);
    if (tabs.length === 0) return;

    restoreActiveTab(section);

    tabs.forEach((tab) => {
      tab.addEventListener(
        'click',
        () => {
          activateTab(section, tab);
        },
        { signal }
      );

      tab.addEventListener(
        'keydown',
        (event) => {
          switch (event.key) {
            case 'ArrowLeft':
              event.preventDefault();
              focusAdjacentTab(section, -1);
              break;
            case 'ArrowRight':
              event.preventDefault();
              focusAdjacentTab(section, 1);
              break;
            case 'Home':
              event.preventDefault();
              getTabs(section)[0]?.focus();
              break;
            case 'End':
              event.preventDefault();
              getTabs(section).at(-1)?.focus();
              break;
            case 'Enter':
            case ' ':
              event.preventDefault();
              activateTab(section, tab);
              break;
          }
        },
        { signal }
      );
    });

    getPanels(section).forEach((panel) => {
      const scroller = panel.querySelector('[data-fill-new-season-products]');
      const previous = panel.querySelector('[data-fill-new-season-prev]');
      const next = panel.querySelector('[data-fill-new-season-next]');

      previous?.addEventListener('click', () => scrollPanel(panel, -1), { signal });
      next?.addEventListener('click', () => scrollPanel(panel, 1), { signal });
      scroller?.addEventListener('scroll', () => setScrollButtonState(panel), { signal, passive: true });
    });

    section.querySelectorAll('.fill-new-season__product-form').forEach((formComponent) => {
      const button = getQuickAddButton(formComponent);

      button?.addEventListener(
        'click',
        (event) => {
          if (button.dataset.fillNewSeasonState === 'loading') {
            event.preventDefault();
            event.stopPropagation();
          }
        },
        { signal }
      );

      formComponent.addEventListener(
        'submit',
        () => {
          window.setTimeout(() => {
            if (!button || button.disabled) return;
            setQuickAddState(button, 'loading');
          }, 0);
        },
        { signal }
      );
    });

    document.addEventListener(
      'shopify:cart:lines-update',
      (event) => {
        event.promise
          ?.then(({ detail }) => {
            const sourceId = detail?.sourceId;
            if (!sourceId) return;

            const formComponent = section.querySelector(`#${CSS.escape(sourceId)}`);
            const button = getQuickAddButton(formComponent);
            if (!button) return;

            setQuickAddState(button, detail?.didError ? 'idle' : 'added');
          })
          .catch(() => {
            resetSectionQuickAdd(section);
          });
      },
      { signal }
    );

    window.addEventListener('resize', () => updateAllScrollButtons(section), { signal });
    requestAnimationFrame(() => updateAllScrollButtons(section));
  }

  function initAll() {
    document.querySelectorAll('[data-fill-new-season-section]').forEach(initSection);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initAll, { once: true });
  } else {
    initAll();
  }

  document.addEventListener('shopify:section:load', (event) => {
    const section = event.target?.querySelector?.('[data-fill-new-season-section]');
    if (section) initSection(section);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    const section = event.target?.querySelector?.('[data-fill-new-season-section]');
    if (!section) return;

    resetSectionQuickAdd(section);
    sectionControllers.get(section)?.abort();
    sectionControllers.delete(section);
  });

  document.addEventListener('shopify:block:select', (event) => {
    const blockId = event.detail?.blockId;
    if (!blockId) return;

    document.querySelectorAll('[data-fill-new-season-section]').forEach((section) => {
      const tab = getTabs(section).find((candidate) => candidate.dataset.fillNewSeasonBlockId === blockId);
      if (tab) {
        activateTab(section, tab, { focus: true });
      }
    });
  });
})();
